import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ToastsManager } from 'ng2-toastr';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BusinessRulesInterface } from '../../../../interfaces/business-rules.interface';
import { BusinessRulesService } from '../../../../services/business-rules.service';
import { BusinessRulesTypeEnum } from '../../../../enums/businessRulesTypes.enum';
import { MatDialog, MatSelectChange } from '@angular/material';
import { RemoveRuleDialogComponent } from '../../remove-rule-dialog/remove-rule-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-business-rules-min-solo-growth',
  templateUrl: './business-rules-minSoloGrowth.component.html',
  styleUrls: ['./business-rules-minSoloGrowth.component.scss']
})
export class BusinessRulesMinSoloGrowthComponent implements OnChanges {
  @Output()
  businessRuleChange = new EventEmitter<BusinessRulesInterface[]>();
  @Output()
  isActivated = new EventEmitter<string>();

  @Input('businessRules')
  set rules(value: BusinessRulesInterface[]) {
    if (!value) {
      this._rules = [];
      return;
    }
    this._rules = value.filter(rule => rule.type === BusinessRulesTypeEnum.MIN_PRICE_GROWTH_SOLO_CUSTOMER);
    this.businessRuleChange.emit(this._rules);
  }

  get rules(): BusinessRulesInterface[] {
    return this._rules;
  }

  private _rules: BusinessRulesInterface[];
  public selectedOption = 'new';
  public businessRules: BusinessRulesInterface;
  public soloForm: FormGroup;
  public selectedRule: BusinessRulesInterface;
  public activeRule = [];
  public inActiveRule = [];
  public productCategory: string[] = [
    'internet',
    'tv',
    'hardware',
    'premium',
    'telephone',
    'mobileInternet',
    'housingCommunity',
    'extra'
  ];
  isSubmitting = false;

  constructor(
    private toastr: ToastsManager,
    private businessRuleService: BusinessRulesService,
    private dialog: MatDialog,
    public translate: TranslateService
  ) {
    this.soloForm = new FormGroup({
      label: new FormControl('', [Validators.required]),
      presentProductCategory: new FormControl('', [Validators.required]),
      presentProductMinRank: new FormControl('', [Validators.required]),
      presentProductMaxRank: new FormControl('', [Validators.required]),
      newProductCategory: new FormControl('', [Validators.required]),
      newProductMinRank: new FormControl('', [Validators.required]),
      newProductMaxRank: new FormControl('', [Validators.required]),
      minServiceLevelGrowth: new FormControl('', [Validators.required]),
      minSurcharge: new FormControl('', [Validators.required]),
      isActive: new FormControl(false)
    });
  }

  ngOnChanges() {
    this.activeRule = this._rules.filter(rule => rule.isActive === true);
    this.inActiveRule = this._rules.filter(rule => rule.isActive === false);
  }

  onRuleChange(event: MatSelectChange): void {
    const rule = event.value;
    if (rule && rule !== 'new') {
      this.selectedRule = rule;
      this.soloForm.patchValue({
        presentProductCategory: rule.minSoloPriceGrowth.actualService.productCategory,
        presentProductMinRank: rule.minSoloPriceGrowth.actualService.minRank,
        presentProductMaxRank: rule.minSoloPriceGrowth.actualService.maxRank,
        newProductCategory: rule.minSoloPriceGrowth.newServices[0].productCategory,
        newProductMinRank: rule.minSoloPriceGrowth.newServices[0].minRank,
        newProductMaxRank: rule.minSoloPriceGrowth.newServices[0].maxRank,
        ...rule.minSoloPriceGrowth,
        isActive: rule.isActive
      });
    } else {
      this.selectedRule = null;
      this.soloForm.reset({
        isActive: false
      });
    }
  }

  saveRule(formData: FormGroup) {
    const rule: BusinessRulesInterface = {
      minSoloPriceGrowth: {
        newServices: [
          {
            productCategory: formData.value.newProductCategory,
            maxRank: formData.value.newProductMaxRank,
            minRank: formData.value.newProductMinRank
          }
        ],
        actualService: {
          productCategory: formData.value.presentProductCategory,
          maxRank: formData.value.presentProductMaxRank,
          minRank: formData.value.presentProductMinRank
        },
        ...formData.getRawValue()
      },
      isActive: formData.value.isActive,
      type: BusinessRulesTypeEnum.MIN_PRICE_GROWTH_SOLO_CUSTOMER
    };
    if (!this.selectedRule) {
      this.isSubmitting = true;

      this.businessRuleService.create(rule).subscribe(
        createdRule => {
          this.selectedRule = createdRule;
          this.rules.push(createdRule);
          this.isActivated.emit();
          this.isSubmitting = false;
          return this.toastr.success(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_SAVED'));
        },
        () => {
          this.isSubmitting = false;
          this.toastr.error(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_SAVE_ERROR'));
        }
      );
      return;
    }
    this.updateRule(rule);
  }

  updateRule(businessRuleToBeUpdated): void {
    this.isSubmitting = true;

    this.businessRuleService.update(businessRuleToBeUpdated, this.selectedRule._id).subscribe(
      businessRule => {
        this.selectedRule = businessRule;
        this.isActivated.emit();
        this.isSubmitting = false;
        return this.toastr.success(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_UPDATED'));
      },
      () => {
        this.isSubmitting = false;
        this.toastr.error(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_UPDATE_ERROR'));
      }
    );
  }

  deleteRuleModal() {
    this.dialog
      .open(RemoveRuleDialogComponent, { disableClose: true })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.deleteRule();
        }
      });
  }

  deleteRule(): void {
    this.isSubmitting = true;

    this.businessRuleService.delete(this.selectedRule._id).subscribe(
      () => {
        this._rules = this._rules.filter(rule => rule._id !== this.selectedRule._id);
        this.isActivated.emit();
        this.toastr.info(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_DELETED'));
        this.soloForm.reset({
          isActive: false
        });
        this.selectedOption = 'new';
        this.selectedRule = null;
        this.isSubmitting = false;
      },
      () => {
        this.toastr.error(this.translate.instant('BUSINESS_RULES_MIN_SOLO_GROWTH.COMPONENT.RULE_DELETE_ERROR'));
        this.isSubmitting = false;
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/business-rules/business-rules-components/business-rules-minSoloGrowth/business-rules-minSoloGrowth.component.ts